import {
  statusListWithCancellationOption,
  EventTypeInterchangeable,
  StatusWithNoneAndCancellation,
  statusWithNoneAndCancellation,
  eventStatusListWorkOrder,
  statusListWithApproverStates,
  StatusListWithApproverStates,
  ShiftEvent,
  ParameterChange,
  Task,
  BaseEvent,
  StatusWithNone,
  StatusWithNoneAndCancellationAndApproverAndWorkOrderStates,
  statusList,
  statusListWithNoneOption,
  SystemChangeRequest
} from '@domain/shifthandover'
import {SharedEventStructure} from '@hconnect/common/types'
import {union} from 'lodash'

// All states that an event can have. This is used on events page 'Status' filter
export const statusListWithAllStates: StatusWithNoneAndCancellationAndApproverAndWorkOrderStates[] =
  union(
    statusListWithCancellationOption,
    statusListWithNoneOption,
    statusWithNoneAndCancellation,
    statusListWithApproverStates,
    eventStatusListWorkOrder
  )

export type Status = (typeof statusList)[number]

export const eventTypeToStatusMapping: {
  [key in EventTypeInterchangeable]: readonly (
    | Status
    | StatusWithNone
    | StatusWithNoneAndCancellation
    | StatusListWithApproverStates
  )[]
} = {
  malfunction: statusList,
  information: statusListWithNoneOption,
  idea: statusListWithNoneOption,
  environment: statusWithNoneAndCancellation,
  quality: statusWithNoneAndCancellation,
  healthAndSafety: statusWithNoneAndCancellation,
  task: statusListWithCancellationOption,
  parameterChange: statusListWithApproverStates,
  systemChangeRequest: statusListWithApproverStates,
  spot: statusListWithNoneOption
}

export interface Id {
  id: string
}

const shiftEventKeys: (keyof Partial<ShiftEvent>)[] = [
  'attachments',
  'id',
  'createdBy',
  'modifiedBy',
  'created',
  'lastModified',
  'lastModifiedStatus',
  'status',
  'category',
  'description',
  'title',
  'equipment',
  'mainEquipment',
  'isHighlighted',
  'processStage',
  'priority',
  'eventType'
]

export type ShiftEventKeys = (typeof shiftEventKeys)[number]

export const sharedEventStructureKeys: (keyof SharedEventStructure | ShiftEventKeys)[] = [
  'schedule',
  ...shiftEventKeys
]

export const taskKeys: (keyof Partial<Task>)[] = [
  'assignees',
  'dueDate',
  'repetitionInfo',
  'shiftId',
  'doNotStartBefore',
  'toggleAssigneeNotification',
  ...(shiftEventKeys as (keyof Task)[])
]

export const parameterChangeKeys: (keyof ParameterChange)[] = [
  'parameterChange',
  ...(shiftEventKeys as (keyof ParameterChange)[])
]
export const systemChangeRequestKeys: (keyof SystemChangeRequest)[] = [
  'parameterChange',
  ...(shiftEventKeys as (keyof SystemChangeRequest)[])
]

/** Known DF permissions related to the Cockpit */
export const Permissions = [
  'VIEW_PLANT_EVENT',
  'CREATE_PLANT_EVENT',
  'VIEW_PLANT_TASK',
  'CREATE_PLANT_TASK',
  'VIEW_SHIFT_REPORT',
  'VIEW_SHIFT_REPORT',
  'EDIT_PLANT_EVENTS',
  'DELETE_PLANT_EVENTS',
  'EDIT_PLANT_TASK',
  'DELETE_PLANT_TASKS',
  'ADD_DOCUMENTS',
  'VIEW_DOCUMENTS',
  'DELETE_DOCUMENTS',
  'ADD_PLANT_EVENT_COMMENTS',
  'EDIT_PLANT_EVENT_COMMENTS',
  'VIEW_PLANT_EVENT_COMMENTS',
  'DELETE_PLANT_EVENT_COMMENTS',
  'DOWNLOAD_SHIFTREPORT',
  'CHANGE_PLANT_EVENT_STATUS',
  'ASSIGN_PLANT_TASK',
  'REASSIGN_PLANT_TASK',
  'FILTER_PLANT_EVENT',
  'SEARCH_PLANT_EVENT',
  'SEND_PLANT_EVENT_TO_SAP',
  'CREATE_SAP_REQUESTS'
] as const
export type CockpitPermission = (typeof Permissions)[number]

// TODO: check if we can remove these reliabilityRoles.
// If not and relevant, add to documentation.
/** Known DF Roles related to RCFA */
export const reliabilityRolls = ['RELIABILITY_ENGINEER', 'RELIABILITY_CONTRIBUTOR'] as const
/** Known DF Roles related to Cockpit */
export const roles = [
  'SHIFT_LEADER',
  'SHIFT_WORKER',
  'SHIFT_REPORT_READ_ONLY',
  ...reliabilityRolls
] as const
export type Role = (typeof roles)[number]

export type EventFormState = {
  originalItem: Partial<BaseEvent>
  item: Partial<BaseEvent>
  showValidation: boolean
  validationError: Map<keyof BaseEvent, string>
}
